import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { navigate } from 'gatsby';
import Layout from './src/components/Layout/layout';

export const isBrowser = () => typeof window !== 'undefined';

const CustomContext = React.createContext({});

export function CustomProvider(props) {
	const [user, setUser] = useState(null);
	const [token, setToken] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);
		console.log('[Auth] useEffect start');
		console.log(
			'[Auth] current path:',
			window.location.pathname + window.location.search
		);
		console.log('[Auth] has ?user param:', urlParams.has('user'));
		console.log(
			'[Auth] has localStorage token:',
			!!localStorage.getItem('authToken')
		);
		if (urlParams.has('user') || localStorage.getItem('authToken')) {
			handleUserStateChange();
		} else {
			setLoading(false);
		}
	}, []);

	//get the cookie by name and return it
	function getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) return parts.pop().split(';').shift();
	}

	// handle the user state on each Auth action and also on page refreshes
	const handleUserStateChange = async () => {
		var localStorageToken = localStorage.getItem('authToken');
		let activeUser = user;

		//get the user object from the url parameters
		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);
		const userVar = urlParams.get('user');

		if (urlParams.has('user')) {
			console.log('[Auth] ?user detected');
			console.log('[Auth] raw token length:', userVar?.length);
			setToken(userVar);
			localStorage.setItem('authToken', userVar);
			var returnURL = sessionStorage.getItem('returnURL');
			console.log('[Auth] returnURL from session:', returnURL);

			try {
				activeUser = jwtDecode(userVar);
				console.log('[Auth] decoded token:', activeUser);
				if (activeUser.exp < Date.now() / 1000) throw 'Error: Expired';

				// Store audience segment and login method in localStorage
				if (
					activeUser.audience_segments &&
					activeUser.audience_segments.length > 0
				) {
					const primarySegment = activeUser.audience_segments[0];
					// Handle both token formats: .name or .attributes.name
					const segmentName =
						primarySegment.name ||
						primarySegment.attributes?.name ||
						primarySegment;
					localStorage.setItem('audienceSegment', segmentName);
				}
				if (activeUser.loginMethod) {
					localStorage.setItem('loginMethod', activeUser.loginMethod);
				}

				// set the user in state
				window.analytics?.identify(activeUser.id, {
					name: activeUser.first_name + ' ' + activeUser.last_name,
					email: activeUser.email,
					userRoles: activeUser.userRoles,
					memberInstitute: activeUser.member_institute,
				});
				window.analytics.track(`Logged in from URL`, {
					loggedInFromURL: returnURL,
					action: 'Clicked',
					location: window.location.href,
				});

				setUser(activeUser);

				// Show success message for AAF login
				const firstName = activeUser.first_name || 'User';
				let audienceSegment = 'Academic Staff'; // Default
				if (
					activeUser.audience_segments &&
					activeUser.audience_segments.length > 0
				) {
					const primarySegment = activeUser.audience_segments[0];
					audienceSegment =
						primarySegment.name ||
						primarySegment.attributes?.name ||
						primarySegment;
				}

				// Show success message (only in browser, not during build)
				if (typeof window !== 'undefined') {
					console.log(
						`[Auth] AAF Login Success: Hi, ${firstName}! Your view is ${audienceSegment}`
					);
				}

				// Check for first login and redirect based on audience segment
				const hasLoggedInBefore = localStorage.getItem('hasLoggedInBefore');
				if (
					!hasLoggedInBefore &&
					activeUser.audience_segments &&
					activeUser.audience_segments.length > 0
				) {
					const primarySegment = activeUser.audience_segments[0];
					// Handle both token formats: .name or .attributes.name
					const segmentName =
						primarySegment.name ||
						primarySegment.attributes?.name ||
						primarySegment;
					console.log(
						'[Auth] First login detected, audience segment:',
						segmentName
					);

					// Set flag to indicate user has logged in before
					localStorage.setItem('hasLoggedInBefore', 'true');

					// Check where the user was before login
					const returnURL = sessionStorage.getItem('returnURL');
					let shouldRedirectToSegmentPage = true;

					if (returnURL && returnURL !== 'null' && returnURL !== 'undefined') {
						try {
							const returnPath = new URL(returnURL).pathname;
							// Only redirect to segment page if user was on home or login-help
							// Handle both /login-help and /login-help/ (with trailing slash)
							if (
								returnPath !== '/' &&
								returnPath !== '/login-help' &&
								returnPath !== '/login-help/'
							) {
								shouldRedirectToSegmentPage = false;
							}
						} catch (error) {
							console.log('[Auth] AAF Login - Error parsing returnURL:', error);
						}
					}

					if (shouldRedirectToSegmentPage) {
						// Redirect based on audience segment
						let redirectPath = '/'; // Default to home
						if (segmentName === 'Academic Staff') {
							redirectPath = '/academics/';
						} else if (segmentName === 'Student') {
							redirectPath = '/students/';
						} else if (segmentName === 'Employer') {
							redirectPath = '/industry/';
						} else if (segmentName === 'WIL Professional Staff') {
							redirectPath = '/professionals/';
						} else if (segmentName === 'Government & Policy Maker') {
							redirectPath = '/government/';
						}

						console.log(
							'[Auth] Redirecting to segment-specific page:',
							redirectPath
						);
						navigate(redirectPath);
						setLoading(false);
						return;
					}
					// If not redirecting to segment page, continue to returnURL logic below
				}
			} catch (error) {
				console.log('[Auth] decode error:', error);
			}

			//remove query parameter from url
			const url = new URL(window.location);
			url.searchParams.delete('user');
			window.history.replaceState(null, null, url);
			if (
				returnURL !== 'null' &&
				returnURL !== 'undefined' &&
				returnURL !== null &&
				returnURL !== undefined &&
				returnURL !== process.env.GATSBY_WEBSITE_URL
			) {
				returnURL = new URL(returnURL).pathname + new URL(returnURL).search;
				console.log('[Auth] navigating back to returnURL:', returnURL);
				navigate(returnURL);
			}

			// set loading to false
			sessionStorage.setItem('returnURL', null);
			setLoading(false);
		} else if (localStorageToken) {
			// set the user session
			console.log(
				'[Auth] Found localStorage token, length:',
				localStorageToken?.length
			);
			try {
				activeUser = jwtDecode(localStorageToken);
				console.log('[Auth] Decoded localStorage token user:', activeUser);
				if (activeUser.exp < Date.now() / 1000) throw 'Error: Expired';
				setToken(localStorageToken);

				// Store audience segment and login method in localStorage
				if (
					activeUser.audience_segments &&
					activeUser.audience_segments.length > 0
				) {
					const primarySegment = activeUser.audience_segments[0];
					localStorage.setItem(
						'audienceSegment',
						primarySegment.name || primarySegment
					);
				}
				if (activeUser.loginMethod) {
					localStorage.setItem('loginMethod', activeUser.loginMethod);
				}

				window.analytics?.identify(activeUser.id, {
					name: activeUser.first_name + ' ' + activeUser.last_name,
					email: activeUser.email,
					userRoles: activeUser.userRoles,
					memberInstitute: activeUser.member_institute,
				});
				setUser(activeUser);
				console.log('[Auth] Set user from localStorage token:', activeUser);
			} catch (error) {
				console.log('[Auth] localStorage token decode error:', error);
				localStorage.removeItem('authToken');
			}
		} else {
			setLoading(false);
		}
	};

	// handle logout action
	const handleLogout = () => {
		localStorage.removeItem('authToken');
		localStorage.removeItem('audienceSegment');
		localStorage.removeItem('loginMethod');
		localStorage.removeItem('hasLoggedInBefore');
		setToken(null);
		setUser(null);
	};

	// handle login action
	const handleLogin = (userData, userToken) => {
		console.log('[Auth] handleLogin called with:', { userData, userToken });
		console.log('[Auth] User data structure:', userData);
		console.log('[Auth] Token length:', userToken?.length);

		localStorage.setItem('authToken', userToken);
		setToken(userToken);

		// Decode the JWT token to get the complete user data including peopleId
		let decodedUser = userData; // fallback to original userData
		try {
			const decodedToken = jwtDecode(userToken);
			console.log('[Auth] Decoded token data:', decodedToken);
			decodedUser = decodedToken;
		} catch (error) {
			console.log(
				'[Auth] Error decoding token, using original userData:',
				error
			);
		}

		setUser(decodedUser);

		// Store audience segment and login method from token
		try {
			if (
				decodedUser.audience_segments &&
				decodedUser.audience_segments.length > 0
			) {
				const primarySegment = decodedUser.audience_segments[0];
				// Handle both token formats: .name or .attributes.name
				const segmentName =
					primarySegment.name ||
					primarySegment.attributes?.name ||
					primarySegment;
				localStorage.setItem('audienceSegment', segmentName);

				// Check for first login and redirect based on audience segment
				const hasLoggedInBefore = localStorage.getItem('hasLoggedInBefore');
				if (!hasLoggedInBefore) {
					// Handle both token formats: .name or .attributes.name
					const segmentName =
						primarySegment.name ||
						primarySegment.attributes?.name ||
						primarySegment;
					console.log(
						'[Auth] First login detected, audience segment:',
						segmentName
					);

					// Set flag to indicate user has logged in before
					localStorage.setItem('hasLoggedInBefore', 'true');

					// Check where the user was before login
					const returnURL = sessionStorage.getItem('returnURL');
					let shouldRedirectToSegmentPage = true;

					if (returnURL && returnURL !== 'null' && returnURL !== 'undefined') {
						try {
							const returnPath = new URL(returnURL).pathname;
							// Only redirect to segment page if user was on home or login-help
							// Handle both /login-help and /login-help/ (with trailing slash)
							if (
								returnPath !== '/' &&
								returnPath !== '/login-help' &&
								returnPath !== '/login-help/'
							) {
								shouldRedirectToSegmentPage = false;
							}
						} catch (error) {
							console.log('[Auth] AAF Login - Error parsing returnURL:', error);
						}
					}

					if (shouldRedirectToSegmentPage) {
						// Redirect based on audience segment
						let redirectPath = '/'; // Default to home
						if (segmentName === 'Academic Staff') {
							redirectPath = '/academics/';
						} else if (segmentName === 'Student') {
							redirectPath = '/students/';
						} else if (segmentName === 'Employer') {
							redirectPath = '/industry/';
						} else if (segmentName === 'WIL Professional Staff') {
							redirectPath = '/professionals/';
						} else if (segmentName === 'Government & Policy Maker') {
							redirectPath = '/government/';
						}

						console.log(
							'[Auth] Redirecting to segment-specific page:',
							redirectPath
						);
						// Use setTimeout to allow the modal to close first
						setTimeout(() => {
							window.location.href = redirectPath;
						}, 1000);
					}
					// If not redirecting to segment page, the existing returnURL logic will handle it
				}
			}
			if (decodedUser.loginMethod) {
				localStorage.setItem('loginMethod', decodedUser.loginMethod);
			}
		} catch (error) {
			console.log('[Auth] Error processing token data:', error);
		}

		console.log('[Auth] After setting user state:', {
			user: decodedUser,
			token: userToken,
		});
	};

	return (
		<CustomContext.Provider
			value={{
				user,
				setUser,
				loading,
				token,
				setToken,
				handleLogout,
				login: handleLogin,
			}}
		>
			<Layout {...props}></Layout>
		</CustomContext.Provider>
	);
}

export default CustomContext;
